import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useQuery, DocumentNode, NetworkStatus } from '@apollo/client';
import {
  DataGridPro,
  GridColDef,
  GridInitialState,
  GridSortModel,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { Checkbox, Typography } from '@mui/material';
import { reportSx, columnsPanelSx } from './styles';
import { AdminToolbar } from './AdminToolbar';
import { SearchAllButton } from './SearchAllButton';

const PAGE_SIZE = 50;

interface ReportProps {
  // Column definitions
  columns: GridColDef[];
  // Use this to set the initial state of the datagrid
  initialState?: GridInitialState;
  // An apollo query that will be made for each requested page
  query: DocumentNode;
  // Should be the name of the GraphQL response key
  reportName: string;
  // Show the toolbar
  toolbar?: boolean;
  // Slot for the download button
  downloadButton?: React.ReactNode;
  // Row click handler
  onRowClick?: (row: GridRowParams) => void;
  // Control whether to show the hide/show columns UI
  visibility?: boolean;
  // The initial sort model, used to control the initial sort order.
  initialSortModel?: { field: string; sort: 'asc' | 'desc' | null };
  // A callback to be called when the data is loaded.
  onDataLoaded?: (data: any) => void;
  // Slot for a title
  title?: React.ReactNode;
  // Additional query variables to pass to the GraphQL query
  additionalQueryVariables?: Record<string, any>;
  // Custom render function for the search bar
  renderCustomSearchBar?: () => React.ReactNode;
}

export function AdminReport({
  reportName,
  initialState = {},
  query,
  columns,
  downloadButton,
  onRowClick,
  visibility = true,
  initialSortModel,
  onDataLoaded,
  title,
  toolbar = false,
  additionalQueryVariables = {},
  renderCustomSearchBar,
}: ReportProps) {
  const mounted = useRef(true);
  const [allRows, setAllRows] = useState<any[]>([]);

  const [sortModel, setSortModel] = useState<GridSortModel>(
    initialSortModel ? [initialSortModel] : []
  );

  const [visibilityModel, setVisibilityModel] = useState<
    Record<string, boolean>
  >({});

  const queryOptions = useMemo(
    () => ({
      variables: {
        after: undefined, // Start from beginning
        first: PAGE_SIZE,
        orderBy: sortModel[0]?.field,
        order: sortModel[0]?.sort,
        ...additionalQueryVariables,
      },
      notifyOnNetworkStatusChange: true,
    }),
    [sortModel, additionalQueryVariables]
  );

  const { loading, data, fetchMore, networkStatus } = useQuery(
    query,
    queryOptions
  );

  const report = data?.[reportName];
  const pageInfo = report?.pageInfo;

  // Reset and update rows when data changes
  useEffect(() => {
    if (data?.[reportName]?.nodes) {
      // For initial load or when sort changes, replace all rows
      // For fetchMore, the updateQuery callback handles merging
      setAllRows(data[reportName].nodes);
    }
  }, [data, reportName, sortModel]);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleRowsScrollEnd = () => {
    if (mounted.current && pageInfo?.hasNextPage && !loading) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
          first: PAGE_SIZE,
          orderBy: sortModel[0]?.field,
          order: sortModel[0]?.sort,
          ...additionalQueryVariables,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          const prevReport = prev[reportName];
          const newReport = fetchMoreResult[reportName];
          return {
            [reportName]: {
              ...newReport,
              nodes: [
                ...(prevReport?.nodes || []),
                ...(newReport?.nodes || []),
              ],
            },
          };
        },
      });
    }
  };

  const rowCountState = report?.totalCount || allRows.length;

  useEffect(() => {
    if (!loading && onDataLoaded) {
      onDataLoaded(data);
    }
  }, [data, loading, onDataLoaded]);

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel([...sortModel]);
  };

  const handleVisibilityModelChange = (
    visibilityModel: Record<string, boolean>
  ) => {
    setVisibilityModel(visibilityModel);
  };

  const [toolbarButtonEl, setToolbarButtonEl] =
    React.useState<HTMLButtonElement | null>(null);

  const renderDownloadButton = () => downloadButton;

  const renderSearchBar = useCallback(() => {
    return renderCustomSearchBar ? (
      renderCustomSearchBar()
    ) : (
      <SearchAllButton />
    );
  }, [renderCustomSearchBar]);

  const renderTitle = () => <Typography variant="h4">{title}</Typography>;

  return (
    <>
      <DataGridPro
        disableRowSelectionOnClick
        rows={allRows}
        columns={columns}
        rowCount={rowCountState}
        hideFooter
        sortingMode="server"
        onRowsScrollEnd={handleRowsScrollEnd}
        onSortModelChange={handleSortModelChange}
        onColumnVisibilityModelChange={handleVisibilityModelChange}
        columnVisibilityModel={visibilityModel}
        loading={loading || networkStatus === NetworkStatus.fetchMore}
        initialState={initialState}
        slots={{
          toolbar: toolbar ? AdminToolbar : null,
          baseSwitch: Checkbox,
        }}
        slotProps={{
          panel: {
            anchorEl: toolbarButtonEl,
          },
          toolbar: {
            setToolbarButtonEl,
            renderDownloadButton: downloadButton
              ? renderDownloadButton
              : undefined,
            renderSearchBar: renderSearchBar,
            renderTitle: title ? renderTitle : undefined,
            visibility,
          },
          columnsPanel: {
            sx: columnsPanelSx,
          },
          baseTextField: { variant: 'outlined' },
        }}
        sx={reportSx}
        pageSizeOptions={[50]}
        onRowClick={(params) => {
          onRowClick && onRowClick(params);
        }}
      />
    </>
  );
}
